import { Box, Button, styled } from "@mui/material";
import MxTypography from "@rf-l1/MxTypography";
import { memo, useState } from "react";
import IconIndicator from "./IconIndicator";
import { useFormPropertySlice } from "../../store/slices/FormPropertySlice";
import { PropertyFormSchema } from "@rf/property/module/components/property-form/util/schemas/property-schema/propertySchema";
import MessageIndicator from "./MessageIndicator";
import {
  ExcludeFieldsOption,
  useAutoLoadErrors
} from "@rf-hooks/useAutoLoadErrors";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import translatePropertyLabels from "../../util/helps/translatePropertyLabels";
import { debounce } from "lodash";

export interface ButtonIndicatorProps {
  label: string;
  id: number;
  step: any;
  fieldNames?: string[];
  disabledRedirect?: boolean;
  fnTranslate: (label: string) => string;
  excludeFields?: ExcludeFieldsOption;
}

const ButtonIndicator = (props: ButtonIndicatorProps) => {
  const { id, label } = props;
  const { pathname } = useLocation();
  const FORM_CONFIG = useFormContext();
  const [errors, setErrors] = useState<{ label: string; value: string }[]>([]);

  const update = debounce((e: any) => {
    setErrors(e);
  }, 200);
  const { status } = useAutoLoadErrors(FORM_CONFIG, {
    onLoadErrors: async errors => {
      let items: { label: string; value: string }[] = [];
      const mainErrors = Object.keys(errors).map(key => ({
        label: key,
        value: errors[key]
      }));
      items = [...mainErrors];

      update([...items]);
    },
    schema: PropertyFormSchema,
    pickFields: props.fieldNames,
    isEdit: pathname.includes("edit"),
    fnTransformLabels: props.fnTranslate,
    excludeFields: props.excludeFields
  });

  return (
    <LayoutButtonIndicator>
      <ButtonIndicatorContent id={id}>
        <MxTypography variant="button">{label}</MxTypography>
        <IconIndicator
          errorsStep={errors}
          status={status.statusString}
          disabledRedirect={props.disabledRedirect}
        />
      </ButtonIndicatorContent>
      <MessageIndicator status={status.statusString} />
    </LayoutButtonIndicator>
  );
};

export default ButtonIndicator;

interface ButtonIndicatorContentProps {
  id: number;
  children: React.ReactNode;
}

const ButtonIndicatorContent = memo(
  ({ children, id }: ButtonIndicatorContentProps) => {
    const { currentStep, setStep } = useFormPropertySlice();
    const active = currentStep === id ? 1 : 0;

    const changeStep = () => {
      const e = document.getElementById("form-layout");
      if (e)
        e.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      setStep(id);
    };
    return (
      <FormSection disableRipple selected={active} onClick={changeStep}>
        {children}
      </FormSection>
    );
  }
);

const LayoutButtonIndicator = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginLeft: "8px"
});

const FormSection = styled(Button)<{ selected: 1 | 0 }>(({ theme, selected }) => ({
  display: "flex",
  gap: theme.spacing(1),
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.2, 2.5),
  backgroundColor: selected ? theme.palette.primary.main : "#f4f4f5",
  color: selected ? "#fff" : theme.palette.primary.main,
  borderSpacing: "0px",
  border: "1px solid #ccc",
  borderRadius: "20px",
  width: "auto",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: selected ? "#1f3c88" : "#f4f4f5"
  },
  "& .circle-header": {
    border: `1px solid ${selected ? "white" : "var(--m-border-01)"}`
  }
}));
